import React, { useEffect, useState } from 'react';
import { format, isValid, parse } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Tabs,
  Tab,
  Table,
  Spinner,
} from 'react-bootstrap';
import history from '../../../services/history';
import api from '../../../services/api';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import {
  motivo,
  etiologia,
  fabricante,
  hospital,
} from '../../../helpers/laudos/marcapasso';
import useAvaliacao from '../../../hooks/useAvaliacao';
import useAvaliacoes from '../../../hooks/useAvaliacoes';
import useMarcapasso from '../../../hooks/useMarcapasso';

const EMPTY_AVALIACOES = [];
const EMPTY_MARCAPASSO = {};

function paraInputDate(texto, mascara) {
  const data = parse(texto, mascara, new Date());
  return isValid(data) ? format(data, 'yyyy-MM-dd') : '';
}

function dataImplanteInput(marca) {
  const valor = marca && marca.DTIMPGER;
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valor)) {
    return paraInputDate(valor.slice(0, 10), 'yyyy-MM-dd');
  }

  const texto = marca && marca.dataGer;
  if (typeof texto === 'string') {
    const brasileiro = paraInputDate(texto, 'dd/MM/yyyy');
    if (brasileiro) return brasileiro;
  }

  return valor instanceof Date && isValid(valor)
    ? format(valor, 'yyyy-MM-dd')
    : '';
}

const memoMotivo = motivo
  .map(i => ({ value: i.CODMOTIVO, label: i.DESCRICAO.toUpperCase() }))
  .sort((a, b) => a.label.localeCompare(b.label));
const memoEtiologia = etiologia
  .map(item => ({
    value: item.CODETIOLOGIA,
    label: item.DESCRICAO.toUpperCase(),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));
const memoFabricante = fabricante
  .map(item => ({
    value: item.CODFABRICANTE,
    label: item.FABRICANTE.toUpperCase(),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));
const memoHospital = hospital
  .map(i => ({ value: i.value, label: i.label.toUpperCase() }))
  .sort((a, b) => a.label.localeCompare(b.label));

const normalizeAvaliacao = data => {
  if (!data) return null;
  return Array.isArray(data) ? data[0] || null : data;
};

export default function FormAvaliacao() {
  const location = useLocation();
  const { codLaudo } = location.state || {};

  const {
    data: avaliacaoData,
    error: avaliacaoError,
    isLoading: isLoadingAvaliacao,
    mutate: mutateAvaliacao,
  } = useAvaliacao(codLaudo);

  const avaliacaoAtual = normalizeAvaliacao(avaliacaoData);
  const codPac = avaliacaoAtual?.CODPAC;
  const codMarca = avaliacaoAtual?.CODMARCA;

  const {
    data: avaliacoesData,
    mutate: mutateAvaliacoes,
  } = useAvaliacoes(codPac);
  const avaliacoes = avaliacoesData || EMPTY_AVALIACOES;

  const {
    data: marcapassoData,
    error: marcapassoError,
    isLoading: isLoadingMarcapasso,
    mutate: mutateMarcapasso,
  } = useMarcapasso(codMarca);

  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState(null);
  const [marcaLaudoEdit, setMarcaLaudo] = useState(null);
  const [guia, setGuia] = useState('');
  const [carta, setCarta] = useState('');
  const [activeKey, setActiveKey] = useState('avaliacao');

  const avaliacao = avaliacaoSelecionada || avaliacaoAtual;
  const marcaLaudo = marcaLaudoEdit || marcapassoData || EMPTY_MARCAPASSO;

  useEffect(() => {
    if (!avaliacaoAtual || avaliacaoSelecionada) return;
    setCarta(avaliacaoAtual.CARTA || '');
    setGuia(avaliacaoAtual.GUIA || '');
  }, [avaliacaoAtual, avaliacaoSelecionada]);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleOpenReport = printing => {
    const id = avaliacao?.CODLAUDO || avaliacao?.CODAVALIACAO || codLaudo;
    history.push({
      pathname: `/report-avaliacao/${id}`,
      state: { printing },
    });
  };

  const handleGravarAvaliacao = async () => {
    try {
      const av = { ...avaliacao, CARTA: carta, GUIA: guia };
      const resp = await api.put(`avaliacao`, av);
      toast.success(resp.data.message || 'Avaliação gravada com sucesso!');
      setAvaliacaoSelecionada(av);
      mutateAvaliacao(Array.isArray(avaliacaoData) ? [av] : av, false);
      mutateAvaliacoes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gravar avaliação.');
    }
  };

  const handleGravarMarcapasso = async () => {
    try {
      const resp = await api.put(`/marcapasso`, marcaLaudo);
      toast.success(resp.data.message || 'Dados de Marcapasso gravados com sucesso!');
      setMarcaLaudo(marcaLaudo);
      mutateMarcapasso(marcaLaudo, false);
      mutateAvaliacoes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gravar marcapasso.');
    }
  };

  const handleAvaliacao = selectedAvaliacao => {
    setCarta(selectedAvaliacao.CARTA || '');
    setGuia(selectedAvaliacao.GUIA || '');
    setAvaliacaoSelecionada(selectedAvaliacao);
  };

  const handleCarregarTemplateDefault = () => {
    // TODO: colocar um Alert, não o window.confirm
    // eslint-disable-next-line no-alert
    if (carta && !window.confirm('Isto substituirá o texto atual pelo modelo padrão. Deseja continuar?')) {
      return;
    }
    setCarta(
      'Queixas: \n\n' +
      'Exame físico: \n\n' +
      'Medicações: \n\n' +
      'Avaliação de limiares:   LCA:              LCV: \n\n' +
      'Condução atrioventricular: \n\n' +
      'Arritmias: \n\n' +
      'Terapias: \n\n' +
      'Longevidade da Bateria:       Impedância-Atrial:                 Ventricular: \n\n' +
      'SEM IMÃ: FR =           ; LP =                 / COM IMÃ: FR =          ; LP =    \n\n' +
      'Programação final:   Modo de Estimulação:           FR Basal:       AVP:        AVS: \n\n' +
      'Átrio:                Ventrículo:      \n\n' +
      'HDX: \n\n' +
      'Conduta: '
    );
  };

  const handleTipoAvaliacao = tipo => {
    switch (tipo) {
      case 'Marcapasso':
      case 'TRC':
      case 'CDI':
      case 'CDI/TRC':
        setMarcaLaudo({ ...marcaLaudo, MODOESTIM: tipo });
        break;
      default:
    }
  };

  const isLoading =
    isLoadingAvaliacao || (Boolean(codMarca) && isLoadingMarcapasso);
  const hasError = avaliacaoError || marcapassoError;

  if (!codLaudo || isLoading || hasError || !avaliacaoAtual) {
    return (
      <Card className="mt-2">
        <LaudoCardHeader title="Avaliação de Implante" />
        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '80vh',
            }}
          >
            <Spinner animation="border" variant="dark" size="lg" />
          </div>
        )}
        {!isLoading && (
          <>
            <span
              className="text-danger text-center d-block"
              style={{ marginTop: 10 }}
            >
              {!codLaudo
                ? 'Laudo não informado. Abra pela lista de atividades.'
                : hasError
                  ? 'Erro ao carregar avaliação! Tente novamente.'
                  : 'Avaliação não encontrada.'}
            </span>
            <Card.Footer className="text-muted text-center">
              <Button variant="warning" onClick={handleGoBack}>
                Voltar
              </Button>
            </Card.Footer>
          </>
        )}
      </Card>
    );
  }

  return (
    <>
      <LaudoCardHeader title="Avaliação de Implante" />
      <Tabs
        activeKey={activeKey}
        onSelect={key => setActiveKey(key)}
        id="tabs-avaliacao"
        className="mb-4 custom-segmented-tabs"
        variant="pills"
        fill
        style={{
          backgroundColor: '#f1f3f5',
          padding: '6px',
          borderRadius: '12px',
          border: '1px solid #dee2e6',
        }}
      >
        {/* TAB 1: AVALIAÇÃO DE IMPLANTE */}
        <Tab eventKey="avaliacao" title="Avaliação de Implante">
          <Row>
            {/* COLUNA ESQUERDA: LISTA DE AVALIAÇÕES */}
            <Col sm={4}>
              <Card className="shadow-sm mb-3">
                <Card.Header className="bg-light">
                  <Card.Title className="mb-0 h6 font-weight-bold">
                    Histórico de Avaliações
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table
                    responsive
                    striped
                    hover
                    size="sm"
                    className="mb-0 text-center"
                    style={{ cursor: 'pointer' }}
                  >
                    <thead className="thead-light">
                      <tr>
                        <th>#</th>
                        <th>Cód.</th>
                        <th>Data</th>
                        <th>Pac.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {avaliacoes?.length > 0 ? (
                        avaliacoes.map(a => {
                          const isSelected =
                            (avaliacaoSelecionada?.CODAVALIACAO || avaliacaoAtual?.CODAVALIACAO) === a.CODAVALIACAO;
                          return (
                            <tr
                              key={a.counter || a.CODAVALIACAO}
                              onClick={() => handleAvaliacao(a)}
                              className={isSelected ? 'table-success font-weight-bold' : ''}
                            >
                              <td>{a.counter}</td>
                              <td>{a.CODAVALIACAO}</td>
                              <td>{a.dataAvaliacao}</td>
                              <td>{a.CODPAC}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-muted py-3">
                            Nenhuma avaliação encontrada.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="bg-light">
                      <tr>
                        <td colSpan="3" className="text-right font-weight-bold">
                          TOTAL:
                        </td>
                        <td className="font-weight-bold text-left">
                          {avaliacoes?.length || 0}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* COLUNA DIREITA: FORMULÁRIO DE EDIÇÃO */}
            <Col sm={8}>
              <Card className="shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                  <Card.Title className="mb-0 h6 font-weight-bold">
                    Edição da Avaliação{' '}
                    {avaliacao?.CODAVALIACAO ? `(#${avaliacao.CODAVALIACAO})` : ''}
                  </Card.Title>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCarregarTemplateDefault}
                    title="Preencher com o esqueleto padrão"
                  >
                    Inserir Modelo Padrão
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Card className="mb-3 bg-light border-0">
                    <Card.Body className="p-2">
                      <Row>
                        <Col sm={6}>
                          <Form.Group className="mb-2" controlId="editNomePAC">
                            <Form.Label className="small font-weight-bold mb-1">
                              Nome do Paciente
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              readOnly
                              defaultValue={avaliacao?.NOMEPAC}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="mb-2" controlId="editNOMEMED">
                            <Form.Label className="small font-weight-bold mb-1">
                              Médico Avaliador
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              readOnly
                              defaultValue={avaliacao?.Avaliou}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={4}>
                          <Form.Group className="mb-0" controlId="editConv">
                            <Form.Label className="small font-weight-bold mb-1">
                              Convênio
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              readOnly
                              defaultValue={avaliacao?.SIGLACONV}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={4}>
                          <Form.Group className="mb-0" controlId="editData">
                            <Form.Label className="small font-weight-bold mb-1">
                              Data
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              readOnly
                              defaultValue={avaliacao?.dataAvaliacao}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={4}>
                          <Form.Group className="mb-0" controlId="editGuia">
                            <Form.Label className="small font-weight-bold mb-1">
                              Nº Guia
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              value={guia || ''}
                              onChange={e => setGuia(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Form.Group className="mb-3" controlId="editCarta">
                    <Form.Label className="font-weight-bold">
                      Descrição da Avaliação
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={18}
                      style={{ fontFamily: 'monospace', fontSize: '13px' }}
                      value={carta || ''}
                      onChange={e => setCarta(e.target.value)}
                    />
                  </Form.Group>

                  {/* BARRA DE AÇÕES (GRAVAR E IMPRIMIR SEMPRE DISPONÍVEIS) */}
                  <div className="d-flex justify-content-end align-items-center gap-2 border-top pt-3">
                    <Button
                      onClick={handleGravarAvaliacao}
                      variant="success"
                      className="px-4 mr-2"
                    >
                      Gravar
                    </Button>
                    <Button
                      variant="outline-primary"
                      className="mr-2"
                      onClick={() => handleOpenReport(false)}
                    >
                      Imprimir
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => handleOpenReport(true)}
                    >
                      Imprimir Timbrado
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* TAB 2: IMPLANTE DE MARCAPASSO */}
        <Tab eventKey="marcapasso" title="Implante de Marcapasso">
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0 h6 text-center font-weight-bold">
                DADOS TÉCNICOS DO MARCAPASSO
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Form noValidate>
                {/* DADOS PESSOAIS / CADASTRO */}
                <Card className="mb-3">
                  <Card.Header className="py-1">
                    <strong className="text-success small">Dados Pessoais & Registro</strong>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col sm={2}>
                        <Form.Group controlId="editCodMarca">
                          <Form.Label className="small mb-1">Cód. Marca</Form.Label>
                          <Form.Control size="sm" readOnly defaultValue={marcaLaudo.CODMARCA} />
                        </Form.Group>
                      </Col>
                      <Col sm={2}>
                        <Form.Group controlId="editDataMarca">
                          <Form.Label className="small mb-1">Data</Form.Label>
                          <Form.Control size="sm" readOnly defaultValue={marcaLaudo.dataMarca} />
                        </Form.Group>
                      </Col>
                      <Col sm={3}>
                        <Form.Group controlId="editHoraMarca">
                          <Form.Label className="small mb-1">Hora Cadastro</Form.Label>
                          <Form.Control size="sm" readOnly defaultValue={marcaLaudo.HORAMARCA} />
                        </Form.Group>
                      </Col>
                      <Col sm={5}>
                        <Form.Group controlId="editConvMarca">
                          <Form.Label className="small mb-1">Convênio</Form.Label>
                          <Form.Control size="sm" readOnly defaultValue={marcaLaudo.SIGLACONV} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col sm={6}>
                        <Form.Group controlId="editPacMarca">
                          <Form.Label className="small mb-1">Paciente</Form.Label>
                          <Form.Control size="sm" readOnly defaultValue={`${marcaLaudo.CODPAC || ''} - ${marcaLaudo.NOMEPAC || ''}`} />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="editMedMarca">
                          <Form.Label className="small mb-1">Médico Responsável</Form.Label>
                          <Form.Control size="sm" readOnly defaultValue={marcaLaudo.NOMEMED} />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* INFORMAÇÕES DO IMPLANTE */}
                <Card className="mb-3">
                  <Card.Header className="py-1">
                    <strong className="text-success small">Informações do Implante</strong>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col sm={4}>
                        <Form.Group controlId="obsHospital">
                          <Form.Label className="small mb-1">Hospital</Form.Label>
                          <Form.Control
                            size="sm"
                            as="select"
                            value={marcaLaudo?.HOSPITAL || ''}
                            onChange={e => {
                              const selected = memoHospital.find(opt => String(opt.value) === String(e.target.value));
                              setMarcaLaudo({ ...marcaLaudo, HOSPITAL: selected ? selected.label : '' });
                            }}
                          >
                            <option value="">Selecione o Hospital...</option>
                            {memoHospital.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col sm={3}>
                        <Form.Group controlId="obsTipo">
                          <Form.Label className="small mb-1">Tipo de Dispositivo</Form.Label>
                          <div className="d-flex gap-2 border rounded p-1 bg-light">
                            {['Marcapasso', 'TRC', 'CDI', 'CDI/TRC'].map(tipo => (
                              <Form.Check
                                key={tipo}
                                inline
                                label={tipo}
                                type="radio"
                                id={tipo}
                                name="tipoDispositivo"
                                checked={marcaLaudo.MODOESTIM === tipo}
                                onChange={e => handleTipoAvaliacao(e.target.id)}
                                className="small mb-0"
                              />
                            ))}
                          </div>
                        </Form.Group>
                      </Col>
                      <Col sm={2}>
                        <Form.Group controlId="obsMotivo">
                          <Form.Label className="small mb-1">Motivo Implante</Form.Label>
                          <Form.Control
                            size="sm"
                            as="select"
                            value={marcaLaudo?.motivo || ''}
                            onChange={e => {
                              const selected = memoMotivo.find(opt => String(opt.value) === String(e.target.value));
                              setMarcaLaudo({ ...marcaLaudo, motivo: selected ? selected.value : '' });
                            }}
                          >
                            <option value="">Selecione...</option>
                            {memoMotivo.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col sm={3}>
                        <Form.Group controlId="obsEtiologia">
                          <Form.Label className="small mb-1">Etiologia</Form.Label>
                          <Form.Control
                            size="sm"
                            as="select"
                            value={marcaLaudo?.etiologia || ''}
                            onChange={e => {
                              const selected = memoEtiologia.find(opt => String(opt.value) === String(e.target.value));
                              setMarcaLaudo({ ...marcaLaudo, etiologia: selected ? selected.value : '' });
                            }}
                          >
                            <option value="">Selecione...</option>
                            {memoEtiologia.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* DADOS DO GERADOR */}
                <Card className="mb-3">
                  <Card.Header className="py-1">
                    <strong className="text-success small">Dados do Gerador</strong>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col sm={3}>
                        <Form.Group controlId="fabGerador">
                          <Form.Label className="small mb-1">Fabricante</Form.Label>
                          <Form.Control
                            size="sm"
                            as="select"
                            value={marcaLaudo?.CODFABGER || ''}
                            onChange={e => setMarcaLaudo({ ...marcaLaudo, CODFABGER: e.target.value })}
                          >
                            <option value="">Selecione...</option>
                            {memoFabricante.map(opt => (
                              <option key={opt.value} value={opt.label}>{opt.label}</option>
                            ))}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col sm={3}>
                        <Form.Group controlId="modGerador">
                          <Form.Label className="small mb-1">Modelo</Form.Label>
                          <Form.Control
                            size="sm"
                            // as="select"
                            value={marcaLaudo?.CODMODGER || ''}
                            onChange={e => setMarcaLaudo({ ...marcaLaudo, CODMODGER: e.target.value })}
                          >
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col sm={3}>
                        <Form.Group controlId="serieGerador">
                          <Form.Label className="small mb-1">Nº Série</Form.Label>
                          <Form.Control
                            size="sm"
                            defaultValue={marcaLaudo.SERIEGER}
                            onChange={e => setMarcaLaudo({ ...marcaLaudo, SERIEGER: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={3}>
                        <Form.Group controlId="dtGerador">
                          <Form.Label className="small mb-1">Dt. Implante</Form.Label>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={dataImplanteInput(marcaLaudo)}
                            onChange={e =>
                              setMarcaLaudo({
                                ...marcaLaudo,
                                DTIMPGER: e.target.value || null,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* BOTÃO SALVAR MARCAPASSO */}
                <div className="d-flex justify-content-end mt-3">
                  <Button variant="success" className="px-4" onClick={handleGravarMarcapasso}>
                    Gravar Marcapasso
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </>
  );
}
